/**
 * Public routes — single source of truth for the SPA.
 *
 * Anything an anonymous (logged-out) visitor is allowed to reach belongs
 * here. Multiple parts of the app rely on this list:
 *
 *   • `/src/lib/api.js`      — auth interceptor: never redirect to /login
 *                              when a 401 fires while the visitor is on
 *                              one of these paths.
 *   • `/src/App.js`          — declarative <Route> tree: a runtime guard
 *                              warns in dev if a public path defined in
 *                              JSX is missing from this list (and vice
 *                              versa).
 *   • Future: SEO/sitemap, prerender filters, Open Graph meta — all should
 *     read from PUBLIC_ROUTES so adding a new public surface in ONE place
 *     propagates everywhere.
 *
 * To add a new public route:
 *   1. Add the entry below (path + optional `dynamic: true` for `:param`
 *      style routes).
 *   2. Add the matching `<Route path="…" element={<…/>} />` to App.js.
 *   3. That's it — auth, redirects, sitemap stay in sync automatically.
 */

export const PUBLIC_ROUTES = Object.freeze([
  // Marketing / static
  { path: "/",               kind: "static",  desc: "Landing page" },
  { path: "/privacy",        kind: "static",  desc: "Privacy policy" },
  { path: "/cookie",         kind: "static",  desc: "Cookie policy" },
  { path: "/termini",        kind: "static",  desc: "Terms of service" },
  { path: "/aggiornamenti",  kind: "static",  desc: "Changelog" },
  { path: "/guida",          kind: "static",  desc: "User guide" },

  // Auth flows (user-facing, but visitor MUST be anonymous to view them)
  { path: "/login",            kind: "auth", desc: "Login" },
  { path: "/register",         kind: "auth", desc: "Register" },
  { path: "/verify-email",     kind: "auth", desc: "Email verification (OTP)" },
  { path: "/forgot-password",  kind: "auth", desc: "Forgot password" },
  { path: "/reset-password",   kind: "auth", desc: "Reset password" },

  // Public share links (sent to clients via WhatsApp / email / IG bio)
  { path: "/cliente/",          kind: "share",  dynamic: true, desc: "Public client share" },
  { path: "/trial/",            kind: "share",  dynamic: true, desc: "Public trial share" },
  { path: "/timeline/share/",   kind: "share",  dynamic: true, desc: "Public timeline share" },
  { path: "/request-quote/",    kind: "share",  dynamic: true, desc: "Public lead form (Lead Machine)" },
]);

// Plain string list — convenient for matchers that want simple includes.
export const PUBLIC_ROUTE_PATHS = PUBLIC_ROUTES.map((r) => r.path);

/**
 * Returns true when the given browser pathname is served to anonymous
 * visitors. Used by:
 *   • api.js auth interceptor → suppress the /login redirect.
 *   • Components that need to switch UX based on visitor context.
 *
 * Match semantics:
 *   • Exact equality:        "/login" matches the static "/login" entry.
 *   • Dynamic prefix:        "/trial/" matches "/trial/abc-123".
 *   • Root:                  "/" matches only "/" (never as a prefix).
 */
export function isOnPublicRoute(pathname) {
  if (!pathname || pathname === "/") return pathname === "/" || pathname === "";
  for (const r of PUBLIC_ROUTES) {
    if (r.path === "/") continue; // handled above
    if (r.dynamic) {
      // "/trial/" matches "/trial/abc" and "/trial" (rare but valid)
      const trimmed = r.path.replace(/\/$/, "");
      if (pathname === trimmed || pathname.startsWith(trimmed + "/")) return true;
    } else {
      // Static — match the path exactly (or with a trailing slash variant)
      if (pathname === r.path || pathname === r.path + "/") return true;
    }
  }
  return false;
}
